'use client';
import * as S from './styles.ts';
import { CtaLink, PostCard } from '@template/components';
import author from '@/lib/images/homepage/author.png';
import { StaticImageData } from 'next/image';
import { useTypedDispatch, useTypedSelector } from '@/redux/helpers/hooks.ts';
import { useEffect } from 'react';
import { getStoreArticles } from '@/redux/slices/store/thunks/getStoreArticles.ts';

type TPost = {
  type: string;
  title: string;
  author: string;
  authorDisplayPicture?: string | StaticImageData;
  url: string;
  date: string;
  minutesToRead?: number;
  color: 'primary' | 'secondary' | 'default';
};

interface IBlogsSectionComponentProps {
  links?: {
    href: string;
    linkText: string;
    color: 'primary' | 'secondary' | 'default';
  }[];
}

const dummyPosts: TPost[] = [
  {
    type: 'blog',
    title: 'Enhance Your Business With BYOD and Mobile VoIP',
    author: 'Adrian',
    authorDisplayPicture: author,
    url: 'https://www.yay.com/blog/voip-phones/byod/',
    date: '2024-03-06',
    minutesToRead: 8,
    color: 'primary',
  },
  {
    type: 'blog',
    title: '5 signs your startup needs a Cloud PBX',
    author: 'Adrian',
    authorDisplayPicture: author,
    url: 'https://www.yay.com/blog/cloud-pbx/business-needs-voip/',
    date: '2024-01-17',
    minutesToRead: 8,
    color: 'primary',
  },
  {
    type: 'news',
    title: 'WLR Stop Sell and the March to VoIP',
    author: 'Adrian',
    authorDisplayPicture: author,
    url: 'https://www.yay.com/news/voip/wholesale-line-rental-wlr-stop-sell/',
    date: '2023-09-05',
    minutesToRead: 8,
    color: 'secondary',
  },
  {
    type: 'news',
    title: 'WLR Stop Sell and the March to VoIP',
    author: 'Adrian',
    authorDisplayPicture: author,
    url: 'https://www.yay.com/news/voip/wholesale-line-rental-wlr-stop-sell/',
    date: '2023-09-05',
    minutesToRead: 8,
    color: 'secondary',
  },
  {
    type: 'news',
    title: 'WLR Stop Sell and the March to VoIP',
    author: 'Adrian',
    authorDisplayPicture: author,
    url: 'https://www.yay.com/news/voip/wholesale-line-rental-wlr-stop-sell/',
    date: '2023-09-05',
    minutesToRead: 8,
    color: 'secondary',
  },
  {
    type: 'news',
    title: 'WLR Stop Sell and the March to VoIP',
    author: 'Adrian',
    authorDisplayPicture: author,
    url: 'https://www.yay.com/news/voip/wholesale-line-rental-wlr-stop-sell/',
    date: '2023-09-05',
    minutesToRead: 8,
    color: 'secondary',
  },
  {
    type: 'news',
    title: 'WLR Stop Sell and the March to VoIP',
    author: 'Adrian',
    authorDisplayPicture: author,
    url: 'https://www.yay.com/news/voip/wholesale-line-rental-wlr-stop-sell/',
    date: '2023-09-05',
    minutesToRead: 8,
    color: 'secondary',
  },
];

const BlogsSectionComponent = ({ links }: IBlogsSectionComponentProps) => {
  const dispatch = useTypedDispatch();
  const { loaded, articles } = useTypedSelector(
    (state) => state.store.articles
  );

  useEffect(() => {
    if (!loaded) {
      dispatch(getStoreArticles());
    }
  }, [loaded]);

  return (
    <S.BlogsSectionWrapper>
      <S.BlogsSectionContainer>
        <S.BlogsSectionHeader>
          <S.BlogsSectionTitle>
            <h2>Latest</h2>
            <S.BlogsSectionSubTitle>Blogs & News</S.BlogsSectionSubTitle>
          </S.BlogsSectionTitle>
          <S.BlogsSectionLinks>
            {links?.map((link, index) => (
              <CtaLink
                key={index}
                href={link.href}
                linkText={link.linkText}
                color={link.color || 'primary'}
              />
            ))}
          </S.BlogsSectionLinks>
        </S.BlogsSectionHeader>
        <S.BlogsSectionContentContainer>
          <S.BlogsSectionContent>
            {loaded
              ? articles.map((post, index) => (
                  <PostCard key={index} {...post} />
                ))
              : dummyPosts.map((post, index) => (
                  <PostCard key={index} {...post} />
                ))}
          </S.BlogsSectionContent>
        </S.BlogsSectionContentContainer>
      </S.BlogsSectionContainer>
    </S.BlogsSectionWrapper>
  );
};

export default BlogsSectionComponent;
export const options = [
  {
    description: '',
    displayName: 'BlogsSectionComponent',
    methods: [],
    props: {
      links: {
        required: false,
        flowType: {
          name: 'Array',
          elements: [
            {
              name: 'signature',
              type: 'object',
              raw: '{\n  href: string;\n  linkText: string;\n  color: "primary" | "secondary" | "default";\n}',
              signature: {
                properties: [
                  {
                    key: 'href',
                    value: {
                      name: 'string',
                      required: true,
                    },
                  },
                  {
                    key: 'linkText',
                    value: {
                      name: 'string',
                      required: true,
                    },
                  },
                  {
                    key: 'color',
                    value: {
                      name: 'union',
                      raw: '"primary" | "secondary" | "default"',
                      elements: [
                        {
                          name: 'literal',
                          value: '"primary"',
                        },
                        {
                          name: 'literal',
                          value: '"secondary"',
                        },
                        {
                          name: 'literal',
                          value: '"default"',
                        },
                      ],
                      required: true,
                    },
                  },
                ],
              },
            },
          ],
          raw: '{\n  href: string;\n  linkText: string;\n  color: "primary" | "secondary" | "default";\n}[]',
        },
        description: '',
      },
      posts: {
        required: false,
        flowType: {
          name: 'Array',
          elements: [
            {
              name: 'signature',
              type: 'object',
              raw: '{\n  type: string;\n  title: string;\n  author: string;\n  authorDisplayPicture?: string | StaticImageData;\n  url: string;\n  date: string;\n  minutesToRead?: number;\n  color: "primary" | "secondary" | "default";\n}',
              signature: {
                properties: [
                  {
                    key: 'type',
                    value: {
                      name: 'string',
                      required: true,
                    },
                  },
                  {
                    key: 'title',
                    value: {
                      name: 'string',
                      required: true,
                    },
                  },
                  {
                    key: 'author',
                    value: {
                      name: 'string',
                      required: true,
                    },
                  },
                  {
                    key: 'authorDisplayPicture',
                    value: {
                      name: 'union',
                      raw: 'string | StaticImageData',
                      elements: [
                        {
                          name: 'string',
                        },
                        {
                          name: 'StaticImageData',
                        },
                      ],
                      required: false,
                    },
                  },
                  {
                    key: 'url',
                    value: {
                      name: 'string',
                      required: true,
                    },
                  },
                  {
                    key: 'date',
                    value: {
                      name: 'string',
                      required: true,
                    },
                  },
                  {
                    key: 'minutesToRead',
                    value: {
                      name: 'number',
                      required: false,
                    },
                  },
                  {
                    key: 'color',
                    value: {
                      name: 'union',
                      raw: '"primary" | "secondary" | "default"',
                      elements: [
                        {
                          name: 'literal',
                          value: '"primary"',
                        },
                        {
                          name: 'literal',
                          value: '"secondary"',
                        },
                        {
                          name: 'literal',
                          value: '"default"',
                        },
                      ],
                      required: true,
                    },
                  },
                ],
              },
            },
          ],
          raw: 'TPost[]',
        },
        description: '',
      },
    },
  },
];
